<template>
    <div 
        @click="onClickCard"
        class="group relative flex flex-col justify-between w-80 sm:w-[340px] p-4 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200/80 dark:border-gray-700/80 shadow-xs hover:shadow-md transition-all duration-200 cursor-pointer shrink-0 select-none overflow-hidden"
    >
        <!-- Top row: Date badge on the left, Title and Time on the right -->
        <div class="flex items-start gap-3 w-full">
            <!-- Calendar Date Badge with Category Color -->
            <div 
                class="flex flex-col items-center justify-center w-12 h-14 rounded-xl text-white shadow-xs shrink-0 select-none transition-transform group-hover:scale-105"
                :class="categoryBadgeBgClass"
                :style="categoryBadgeBgStyle"
            >
                <span class="text-[10px] font-bold uppercase tracking-wider leading-tight opacity-95">
                    {{ weekDayShort }}
                </span>
                <span class="text-xl font-black leading-tight mt-0.5">
                    {{ monthDayNumber }}
                </span>
            </div>

            <!-- Title & Time / Details -->
            <div class="flex-1 min-w-0 pt-0.5">
                <h4 class="font-bold text-gray-900 dark:text-white text-base leading-snug truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors" :title="event.title">
                    {{ event.title }}
                </h4>
                <div class="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1.5 flex-wrap truncate">
                    <span v-if="timeText" class="font-medium text-gray-600 dark:text-gray-300">{{ timeText }}</span>
                    <span v-if="timeText && songsText" class="text-gray-300 dark:text-gray-600">•</span>
                    <span v-if="songsText">{{ songsText }}</span>
                    <span v-if="withoutChordsCount > 0" class="text-amber-600 dark:text-amber-400 font-medium">
                        , {{ $t('dashboard.withoutChords', { count: withoutChordsCount }) }}
                    </span>
                </div>
            </div>
        </div>

        <!-- Bottom row: Category Chip (Left) & "Abrir agenda" Button (Right) -->
        <div class="flex items-center justify-between gap-2 mt-4 pt-2">
            <!-- Left: Category chip filled with event category color -->
            <div class="flex items-center min-w-0">
                <span 
                    class="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold text-white shadow-2xs truncate max-w-[170px]"
                    :class="categoryChipClass"
                    :style="categoryChipStyle"
                >
                    {{ categoryDisplayName }}
                </span>
            </div>

            <!-- Right button: "Abrir agenda" (opens the event in schedule view) -->
            <button 
                type="button"
                @click.stop="openScheduleEvent"
                class="inline-flex items-center justify-center px-3.5 py-1.5 rounded-xl text-xs font-semibold text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-650 hover:border-gray-400 active:scale-95 transition-all shadow-2xs shrink-0 cursor-pointer"
            >
                <span>{{ $t('dashboard.viewSchedule', 'Abrir agenda') }}</span>
            </button>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, onMounted, defineProps, defineEmits } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { isTailwindColor } from '@/utilities/color'
import PlaylistService from '@/service/playlist.service'

const router = useRouter()
const { t, locale } = useI18n()

const props = defineProps({
    event: {
        type: Object,
        required: true
    },
    categories: {
        type: Array,
        default: () => []
    }
})

const emit = defineEmits(['click', 'open-event'])

const playlistDetails = ref(null)

const currentCategory = computed(() => {
    return props.categories.find(c => c.name === props.event.category || c.id === props.event.categoryId)
})

const categoryColor = computed(() => {
    const rawColor = currentCategory.value?.color || props.event.categoryData?.color
    if (rawColor) return rawColor
    // Fallback based on category name
    const catName = props.event.category?.toLowerCase() || ''
    if (catName.includes('ensaio') || catName.includes('rehearsal')) return 'emerald'
    if (catName.includes('geral') || catName.includes('general')) return 'purple'
    if (catName.includes('live') || catName.includes('apresenta')) return 'red'
    if (catName.includes('personal') || catName.includes('pessoal')) return 'indigo'
    return 'purple'
})

// Normalized Tailwind color mapping for solid backgrounds
const tailwindColorClass = computed(() => {
    const c = categoryColor.value?.toLowerCase()
    if (!c) return 'bg-purple-600'
    if (c === 'green') return 'bg-emerald-700'
    if (c === 'emerald') return 'bg-emerald-700'
    if (c === 'purple') return 'bg-purple-600'
    if (c === 'indigo') return 'bg-indigo-600'
    if (c === 'blue') return 'bg-blue-600'
    if (c === 'red') return 'bg-rose-600'
    if (c === 'amber' || c === 'yellow') return 'bg-amber-600'
    if (isTailwindColor(c)) return `bg-${c}-600`
    return ''
})

const categoryBadgeBgClass = computed(() => {
    return tailwindColorClass.value
})

const categoryBadgeBgStyle = computed(() => {
    const c = categoryColor.value
    if (!isTailwindColor(c?.toLowerCase())) {
        return { backgroundColor: c }
    }
    return {}
})

const categoryChipClass = computed(() => {
    return tailwindColorClass.value
})

const categoryChipStyle = computed(() => {
    const c = categoryColor.value
    if (!isTailwindColor(c?.toLowerCase())) {
        return { backgroundColor: c }
    }
    return {}
})

const categoryDisplayName = computed(() => {
    const cat = props.event.category || 'General'
    return t('schedule.categories.' + cat, cat)
})

const eventDateObj = computed(() => {
    if (props.event.startTime) return new Date(props.event.startTime)
    if (props.event.date) return new Date(props.event.date)
    return new Date()
})

const weekDayShort = computed(() => {
    const d = eventDateObj.value
    const currentLocale = locale.value || 'pt-BR'
    const shortStr = d.toLocaleDateString(currentLocale, { weekday: 'short' })
    return shortStr.replace('.', '').toUpperCase()
})

const monthDayNumber = computed(() => {
    return eventDateObj.value.getDate()
})

const timeText = computed(() => {
    if (!props.event.startTime) return ''
    const start = new Date(props.event.startTime)
    const currentLocale = locale.value || 'pt-BR'
    const is12Hour = currentLocale.startsWith('en')
    return start.toLocaleTimeString(currentLocale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: is12Hour
    })
})

const firstPlaylistObj = computed(() => {
    if (!props.event?.playlists || props.event.playlists.length === 0) return null
    const first = props.event.playlists[0]
    if (typeof first === 'object' && first !== null) return first
    return { id: String(first), _id: String(first) }
})

const songsList = computed(() => {
    if (playlistDetails.value?.songs && Array.isArray(playlistDetails.value.songs)) {
        return playlistDetails.value.songs
    }
    if (firstPlaylistObj.value?.songs && Array.isArray(firstPlaylistObj.value.songs)) {
        return firstPlaylistObj.value.songs
    }
    return []
})

const totalSongsCount = computed(() => {
    if (songsList.value.length > 0) return songsList.value.length
    if (typeof playlistDetails.value?.songCount === 'number') return playlistDetails.value.songCount
    if (typeof firstPlaylistObj.value?.songCount === 'number') return firstPlaylistObj.value.songCount
    return 0
})

const withoutChordsCount = computed(() => {
    if (songsList.value.length === 0) return 0
    return songsList.value.filter(song => {
        const hasChordProp = song.chord !== undefined && song.chord !== null && song.chord !== -1 && song.chord !== 0
        const hasContentChords = song.data?.content?.includes('data-chord') || 
                                song.modifiedLyric?.includes('data-chord') ||
                                song.data?.lyric?.includes('data-chord')
        return !hasChordProp && !hasContentChords
    }).length
})

const songsText = computed(() => {
    const count = totalSongsCount.value
    if (count === 0) return ''
    if (count === 1) return t('dashboard.oneSong', '1 música')
    return t('dashboard.songsCount', { count })
})

const openScheduleEvent = () => {
    const eventId = props.event.id || props.event._id
    emit('open-event', props.event)
    router.push({
        path: '/schedule',
        query: eventId ? { eventId } : {}
    })
}

const onClickCard = () => {
    openScheduleEvent()
}

onMounted(async () => {
    const pId = firstPlaylistObj.value?.id || firstPlaylistObj.value?._id
    if (pId) {
        try {
            const cached = await PlaylistService.getCachedPlaylist(pId)
            if (cached) playlistDetails.value = cached
        } catch (err) {
            console.warn('[DashboardEventCard] Error fetching cached playlist:', err)
        }
    }
})
</script>
